import { getModel } from '../../model/registry'
import { useEffect, useRef, useState } from "react";
import useApi from "../../hooks/useApi";
import { useNavigate, useParams } from "react-router-dom";
import { useQueryClient, useMutation } from "@tanstack/react-query";
import useTranslation from "../../hooks/useTranslation";
import pause from "../../utils/pause";

//Form State
import useFormValidation from "./useFormValidation";
import useFormModal from "../modal/useModalDialog";
import updateObject from "../../utils/updateObject";
import generateEndpoint from "../../utils/generateEndpoint";
import { useMessage } from "../pageView/Page";
import useSettings from "../../hooks/useSettings";
import { useRefreshLater } from "../../hooks/useRefreshLater";
import invalidateListQueries from "../../utils/invalidateListQueries";
import { toGuardMessage } from "../../utils/guardMessage";
import useWorkQueue from "./useWorkQueue";

//Runs the Validation Logic
import runValidationLogic from '../../../../common/logic/runValidationLogic.js'
import { useStore } from "../pageView/Page";

// Helper functions
function convertLineBreaksToHTML(text) {
  return text.replace(/\n/g, "<br>");
}

function generateUrl(target, id) {
  return target.replace(':id', id);
}


const invalidateMainFormQueries = async (endpoint, queryClient) => {
  // For subcollections (e.g. paymentAccount/[id]/paymentTransaction),
  // also invalidate the parent entity after a delay
  if (endpoint && endpoint.includes('/')) {
    const segments = endpoint.split('/')
    if (segments.length >= 2) {
      const parentEntity = segments[0]
      const parentId = segments[1]
      const qKey = [parentEntity, parentId]
      // Wait for the backend event to process
      await pause(3000)

      // Invalidate the parent entity query and its list
      await queryClient.invalidateQueries({ queryKey: qKey })
      await queryClient.invalidateQueries({ queryKey: ['list', parentEntity] })
    }
  }

};


const useFormActions = (mode, config, { onMove } = {}) => {
  //console.log('useFormActions: ', config)
  const [showMoveModal, setShowMoveModal] = useState(false)

  //Setting up the State & validation
  const [state, setState] = useStore((s) => s)
  //const { setValidated } = useFormValidation()
  const { setValidated } = useFormValidation(config.entity)
  const [message, setMessage] = useMessage((s) => s)
  const { setShowModal, setModalConfig } = useFormModal()
  const { initializeSettings } = useSettings()

  const refreshLater = useRefreshLater();


  //Id of the entity shown taken from the URL
  const { id, id2 } = useParams()

  //this formats the endpoint to use the first id in the main route in case of a subroute
  let endpoint;
  if (config && config.path !== undefined) {
    endpoint = generateEndpoint(config.path, id);
  }


  //Setting up API and Translation
  const { getMe, deleteMe, putMe, postMe } = useApi()

  //Sets up Navigation, QueryClient & back OnSubmit Function
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  // Where to go once a save succeeds. `stay` keeps the user on the card;
  // `navigateTo` is a route prefix that replaces the default "go back". They are
  // not exclusive — `warehouseOrderCard` sets both, because `stay` only gates the
  // save path while `navigateTo` is still honoured after a card-title action.
  // A new record reopens once saved, unless `reopenNew: false` — then saving
  // closes it, like saving an existing one.
  const { stay = false, navigateTo = false, reopenNew = true } = config?.afterSubmit ?? {}

  const { t, m } = useTranslation()

  // A page in a work queue (38.04): saving a new record opens the next one.
  const workQueue = useWorkQueue(config?.queue)

  const handleNavigation = (executionMode, response) => {
    if (executionMode === 'new') {
      navigate(reopenNew ? `${navigateTo}/${response.data.id}` : `${navigateTo}/`)
    }
    if (executionMode === 'edit') {
      navigate(`${navigateTo}/`)
    }
  }

  const handleSettings = (endpoint) => {
    initializeSettings(endpoint)
  }

  //01 async function called by mutateForm function
  const saveFormFn = async (option) => {

    //Checks if we are saving a settings, then it updates the settings
    if (endpoint.toLowerCase().includes("settings")) handleSettings(endpoint)

    let response
    let executionMode = mode
    if (option.mode) executionMode = option.mode

    setValidated(true)
    // A save can take seconds — a new organisation sets up its whole chart of
    // accounts — and without a sign of it the user tried again.
    setMessage({ show: true, title: 'all.form.saving', type: 'info', message: '' })

    if (executionMode === 'edit' || executionMode === 'confirm') {
      response = await putMe(endpoint, state, state.id)
    } else if (executionMode === 'new') {
      response = await postMe(endpoint, state)
    }
    if (response && !response.error) {
      let title = t('success.updated')
      setMessage({ show: true, title: title, type: 'success', message: '' })
      await pause(1200)
      if (option.invalidateQuery) {
        const resolvedInvalidateQuery = option.invalidateQuery.replace(':id', id)
        await queryClient.invalidateQueries({
          predicate: (query) => query.queryKey.some((part) => part === resolvedInvalidateQuery)
        })
      }

      await invalidateListQueries(endpoint, queryClient)
      await queryClient.invalidateQueries({ queryKey: [endpoint] })

      if (!stay) {
        if (config.queue && executionMode === 'new') await workQueue.advance()
        else if (navigateTo) handleNavigation(executionMode, response)
        else navigate(-1)
      }
    } else if (response?.error) {
      let title = t(response.error.name)
      let message = response.error.message
      setMessage({ show: true, title: title, type: 'error', message: message })
    }
    return ({ action: 'completed' })
  }

  //02 Delete Function
  const deleteFormFn = async () => {
    setShowModal(false)
    setMessage({ show: true, title: 'all.form.deleting', type: 'info', message: '' })
    const response = await deleteMe(endpoint, state.id)
    // Blocked deletes arrive either as a preventDelete object or as a guard's
    // { error, title } key pair — toGuardMessage normalises both. MessageBar
    // translates the keys, so they are passed through raw.
    const guardMessage = toGuardMessage(response, 'error')
    if (guardMessage) {
      setMessage(guardMessage)
      await pause(1200)
    } else {
      // Invalidate the current entity's query and its list
      await queryClient.invalidateQueries([endpoint])
      await invalidateListQueries(endpoint, queryClient)

      // For subcollections, also invalidate the parent entity after a delay
      await invalidateMainFormQueries(endpoint, queryClient)

      if (config.entity === 'organization') {
        await queryClient.invalidateQueries(['userOrgs'])
        navigate('/', { replace: true })
      } else {
        navigate(-1)
      }
    }
    return response
  }

  // 03 Generic Post Action function
  const postActionFn = async (option) => {
    setShowModal(false)

    const response = await postMe(`${endpoint}/${state.id}/${option.action}`, state)
    if (response.status === 'success') {
      const actions = config.cardTitle.actions
      const title = actions[option.action].message
      setMessage({ show: true, title: title, type: 'success', message: '', action: '' })
      await pause(1000)
      if (option.invalidateQuery) {
        const resolvedInvalidateQuery = option.invalidateQuery.replace(':id', id)
        await queryClient.invalidateQueries({
          predicate: (query) => query.queryKey.some((part) => part === resolvedInvalidateQuery)
        })
      }
      await invalidateListQueries(endpoint, queryClient)
      const actionConfig = config.cardTitle?.actions?.[option.action]
      if (actionConfig?.reload) {
        await queryClient.invalidateQueries({ queryKey: [endpoint] })
      } else if (navigateTo) {
        handleNavigation('edit', response)
      } else navigate(-1)
    }
    else if (response.status === 'error') {
      const title = m(response?.title)
      const message = m(response.message, response.params)
      // Only an error that offers a follow-up carries `action`; formatting a
      // missing key would print "Undefined" under every other error.
      const action = response?.action?.message
        ? { message: m(response.action.message, [response?.nextValue]), formChanges: response.action.formChanges }
        : ''
      setMessage({ show: true, title: title, type: 'warning', message: message, action: action })
      await pause(1000)
    } else if (response.status === 'warning') {
      const title = m(response.message, response.params)
      setMessage({ show: true, title: title, type: 'warning', message: '', action: '' })
      await pause(2000)
      if (config.entity === 'organization') { navigate('/', { replace: true }) } else {
        navigate(-1)
      }
    }

    //This does a delayed query invalidation if configured.
    if (config.refresh) refreshLater(config.refresh, state.id, option.action)
    //Necessary so the mutation doesn't fail 
    return response
  }


  //04 Send Function
  const sendFormFn = async (option) => {
    const payload = option.payload
    const msg = { ...payload.config.msg, ...payload.data }
    msg.from = "Velto@velto.io"
    msg.message = convertLineBreaksToHTML(msg.message)
    const data = { ...state }
    const mailBody = { data: data, msg: msg }
    await postMe(`email`, mailBody)
    setShowModal(false)
    setMessage({ show: true, title: 'email Sent', type: 'success', message: 'Your email is on its way' })
    await pause(1000)
  }

  const goTo = (target) => {
    const url = generateUrl(target, state.id)
    navigate(url)
  }

  //This is the function that decides what gets triggered
  const actionHandler = async (option) => {
    let response

    const result = await runValidationLogic(state.entity, option.action, state, getMe)

    if (!result.valid) {
      setMessage(toGuardMessage(result))
      setShowModal(false)
      return { msg: 'Validation Error' }
    }

    // Config-driven dispatch — actionType on the option takes precedence over the switch
    if (option.actionType === 'goTo') return goTo(option.target)
    if (option.actionType === 'openUrl') return window.open(state.url, '_blank', 'noopener,noreferrer')
    if (option.actionType === 'post') return await postActionFn(option)

    switch (option.action) {
      // Awaited, so the action — and the card's busy state — lasts until the
      // record is really gone.
      case 'delete': response = await deleteFormFn()
        break;
      case 'book': await postActionFn(option)
        break;
      case 'archive': await postActionFn(option)
        break;
      case 'unarchive': await postActionFn(option)
        break;
      case 'updateItems': await postActionFn(option)
        break;
      case 'save': response = await saveFormFn(option)
        break;
      case 'send': await sendFormFn(option)
        break;
      case 'openUrl':
        if (state.url) window.open(state.url, '_blank', 'noopener,noreferrer')
        break;
      case 'close': navigate(-1)
        break;
      default: await postActionFn(option)
        break;
    }
    return response
  }

  //All actions will run through Form Mutation     
  const formMutation = useMutation({
    mutationFn: actionHandler
  })
  const busyRef = useRef(false)

  // Handle mutation success and error with useEffect since onSuccess/onError are deprecated in v5
  useEffect(() => {
    if (formMutation.isSuccess) {
      const handleSuccess = async () => {
        // Invalidate the current entity's query and its list
        await queryClient.invalidateQueries({ queryKey: [`${endpoint}`] })
        await invalidateListQueries(endpoint, queryClient)

        //Invalidate the main form queries - in case a subcollection is updated.
        await invalidateMainFormQueries(endpoint, queryClient)

        //Reload user Organizations if organization is changed
        if (config.entity === 'organization') {
          await queryClient.invalidateQueries({ queryKey: [`userOrgs`] })
        }
      }
      handleSuccess()
    }
  }, [formMutation.isSuccess, endpoint, queryClient, config.entity])

  useEffect(() => {
    if (formMutation.isError) {
      const error = formMutation.error
      // Only show generic error if we don't have a structured response
      if (!error?.status) {
        setMessage({ show: true, message: 'There was an error!', type: 'error' })
      }
    }
  }, [formMutation.isError, formMutation.error, setMessage])

  // Handle form Change Function
  const handleFormChange = (formChanges) => {

    const updatedData = updateObject(state, formChanges)
    setState(updatedData)

    putMe(endpoint, updatedData, updatedData.id)
    setMessage({ show: false })
  }

  // Run Pre FLight Ops
  const runPreflight = async (option) => {
    setShowModal(false)

    //Check if the action can be posted using: ?trial=true
    const response = await postMe(`${endpoint}/${state.id}/${option.action}?trial=true`, state)

    //If it can run, just show the Confirmation
    if (response.status === 'success') {
      setModalConfig(getModel('modalDialogData')[option.modalConfig])
      setShowModal(true)
    } else if (response.status === 'error') {
      if (response.action?.formChanges) {
        // Handle the need for change (e.g. invoice corrections before booking)
        handleFormChange(response.action.formChanges)
        const modalDialog = getModel('modalDialogData')[option.modalPreflight]
        modalDialog.body = [m(response.message, response.params)]
        setModalConfig(modalDialog)
        setShowModal(true)
      } else {
        // Validation rejection — show error message, do not open modal
        setMessage({ show: true, message: response.message, title: response.title, type: 'error' })
      }
    }
  }

  const runConfirm = async (option) => {
    //Fills the modal with the needed data. 
    setModalConfig(getModel('modalDialogData')[option.modalConfig])
    setShowModal(true)

  }


  // Move-to-folder: PUT only the folder fields, then refresh in-place (no navigate)
  const confirmMove = async (folder) => {
    const response = await putMe(endpoint, {
      folderId: folder.id,
      folderName: folder.name,
      folderPath: folder.path,
    }, state.id)

    if (!response?.error) {
      setState({ ...state, folderId: folder.id, folderName: folder.name, folderPath: folder.path })
      await invalidateListQueries(endpoint, queryClient)
      await queryClient.invalidateQueries({ queryKey: [endpoint] })
    }
    setShowMoveModal(false)
  }

  //This is the entry function that runs Preflight, Confirm and then ActionHandler
  const handleActions = async (option) => {

    // Move opens the folder-picker modal — no mutation needed
    if (option.action === 'move') {
      setShowMoveModal(true)
      return
    }

    let response
    //01 Runs the preflight first
    if (option.preflight) runPreflight(option)
    //02 Runs the confirm Next else runs the action = option.action
    if (option.confirm) { runConfirm(option) }
    else if (!option.preflight) {
      /*
       * One action at a time. A second click while a save or delete is still
       * running started another: two organisations with one name, and two
       * deletes of the same record running side by side.
       */
      if (busyRef.current) return
      busyRef.current = true
      try {
        // Run the Form-Mutation to process the actions -> action handler
        response = await formMutation.mutateAsync(option)
      } finally {
        busyRef.current = false
      }
    }

    //Remove Query Params
    const removeQueryParams = option?.removeQueryParams;

    if (removeQueryParams?.length) {
      queryClient.removeQueries({
        predicate: (query) =>
          query.queryKey.some((part) =>
            removeQueryParams.includes(part)
          ),
      });
    }



    return response
  }

  return {
    handleActions,
    handleFormChange,
    showMoveModal,
    setShowMoveModal,
    confirmMove,
    queueStep: workQueue.step,
    // An action is running: the card title disables its buttons.
    busy: formMutation.isPending,
  };
}

export default useFormActions;
